import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, MapPin, ShieldCheck, Building2, Factory, Truck, Plane, Network, Zap } from "lucide-react";
import { PlatformShellHeader, PlatformShellFooter, useSeo } from "./PlatformShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const USE_ICONS = [Building2, Factory, Truck, Plane, Network, Zap];

export default function DealerCity() {
  const { city } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let mounted = true;
    setStatus("loading");
    axios.get(`${API}/cities/${city}`).then((r) => {
      if (!mounted) return;
      setData(r.data);
      setStatus("ok");
    }).catch(() => { if (mounted) setStatus("missing"); });
    return () => { mounted = false; };
  }, [city]);

  useSeo(data ? {
    title: data.kind === "state"
      ? `Boom Barrier Dealer in ${data.name} | Automatic Boom Barriers · Gate Automation · RFID/ANPR | Boom Barrier Company`
      : `Boom Barrier Dealer in ${data.name}, ${data.state} | Automatic Boom Barriers · Gate Automation | Boom Barrier Company`,
    description: data.kind === "state"
      ? `Automatic boom barriers, gate automation and RFID/ANPR/FASTag access control across ${data.name}. BBC Pro · HDS · Flash — factory-tested, QR-traceable, site-specific quotes within one business day.`
      : `Automatic boom barriers, gate automation and RFID/ANPR access control in ${data.name}, ${data.state}. BBC Pro · HDS · Flash — factory-tested, QR-traceable, site-specific quotes within one business day.`,
    canonical: `https://www.boombarriercompany.com/dealers/${data.slug}`,
    image: "https://www.boombarriercompany.com/branded/bbc-pro.png",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": `Boom Barrier Company — ${data.name}`,
      "url": `https://www.boombarriercompany.com/dealers/${data.slug}`,
      "image": "https://www.boombarriercompany.com/branded/bbc-pro.png",
      "areaServed": data.kind === "state"
        ? { "@type": "State", "name": data.name, "containedInPlace": { "@type": "Country", "name": "India" } }
        : { "@type": "City", "name": data.name },
      "address": {
        "@type": "PostalAddress",
        ...(data.kind === "city" ? { "addressLocality": data.name } : {}),
        "addressRegion": data.state,
        "addressCountry": "IN",
      },
      "priceRange": "₹₹",
      "description": data.kind === "state"
        ? `Automatic boom barriers and gate automation across ${data.name}. Three base models: BBC Pro, BBC HDS, BBC Flash. RFID, ANPR and FASTag integration. Response SLA: ${data.response_sla}.`
        : `Automatic boom barriers and gate automation in ${data.name}, ${data.state}. Three base models: BBC Pro, BBC HDS, BBC Flash. RFID, ANPR and FASTag integration. Response SLA: ${data.response_sla}.`,
    },
  } : { title: "Boom Barrier Dealer — Boom Barrier Company" });

  return (
    <div data-testid="dealer-city-root" className="min-h-screen bg-[#0F1113] text-white font-body antialiased">
      <PlatformShellHeader />

      {status === "loading" && (
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 font-mono text-[11px] uppercase tracking-[0.22em] text-[#71717A]">Loading…</div>
      )}

      {status === "missing" && (
        <div data-testid="city-missing" className="max-w-3xl mx-auto px-6 md:px-12 py-24">
          <h1 className="font-display text-white text-4xl font-black tracking-tight">We don't have a page for this city yet.</h1>
          <p className="mt-4 text-[#A1A1AA]">Submit an enquiry and we'll route you to the nearest BBC partner.</p>
          <Link to="/quote" className="mt-6 inline-flex items-center gap-2 bg-[#00FF85] hover:bg-[#00CC6A] text-black font-semibold px-5 py-3 text-xs uppercase tracking-wider">
            Request a Quote <ArrowRight size={14} />
          </Link>
        </div>
      )}

      {status === "ok" && data && (
        <>
          {/* HERO */}
          <section className="relative pt-20 md:pt-28 pb-16 border-b border-white/5 overflow-hidden">
            <div className="absolute inset-0 bbc-grid-bg opacity-[0.06] pointer-events-none" />
            <div
              className="absolute -top-32 -right-32 w-[700px] h-[700px] rounded-full blur-3xl pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(0,255,133,0.08), transparent 60%)" }}
            />
            <div className="relative max-w-7xl mx-auto px-6 md:px-12">
              <div className="flex items-center gap-3">
                <MapPin size={14} className="text-[#00FF85]" />
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00FF85]">
                  {data.kind === "state" ? `${data.name} · India` : `${data.state} · India`}
                </span>
              </div>
              <h1 className="font-display mt-6 text-white text-5xl md:text-6xl lg:text-7xl font-black tracking-[-0.02em] leading-[0.98] max-w-5xl">
                Boom Barriers
                <br />
                <span className="text-[#00FF85]">{data.kind === "state" ? `across ${data.name}.` : `in ${data.name}.`}</span>
              </h1>
              <p className="mt-6 max-w-2xl text-[#A1A1AA] text-base md:text-lg font-body leading-relaxed">
                {data.intro}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/quote"
                  data-testid="city-cta-quote"
                  className="inline-flex items-center gap-3 bg-[#00FF85] hover:bg-[#00CC6A] text-black font-semibold px-6 py-3.5 text-xs uppercase tracking-wider shadow-[0_0_24px_rgba(0,255,133,0.35)] transition"
                >
                  Request a Quote in {data.name} <ArrowRight size={14} />
                </Link>
                <Link
                  to="/#portfolio"
                  className="inline-flex items-center gap-3 border border-white/15 hover:border-white text-white font-semibold px-6 py-3.5 text-xs uppercase tracking-wider transition"
                >
                  View All Models
                </Link>
              </div>
            </div>
          </section>

          {/* MODELS QUICK STRIP */}
          <section className="py-16 md:py-20 border-b border-white/5">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <h2 className="font-display text-white text-3xl md:text-4xl font-black tracking-[-0.02em]">
                Available in {data.name}
              </h2>
              <div className="mt-10 grid md:grid-cols-3 gap-3">
                {[
                  { name: "BBC Pro", role: "Residential & Commercial", img: "/branded/bbc-pro.png" },
                  { name: "BBC HDS", role: "Industrial & Toll Plaza", img: "/branded/bbc-hds.png" },
                  { name: "BBC Flash", role: "Ultra-Fast / Corporate", img: "/branded/bbc-flash.png" },
                ].map((m) => (
                  <div key={m.name} data-testid={`city-model-${m.name}`} className="border border-white/10 bg-[#16191C] overflow-hidden">
                    <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-[#13161A] to-black">
                      <img src={m.img} alt={`${m.name} automatic boom barrier in ${data.name}`} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-85" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    </div>
                    <div className="p-6">
                      <h3 className="font-display text-white text-xl font-bold tracking-tight">{m.name}</h3>
                      <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[#00FF85]">{m.role}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* USE CASES */}
          <section className="py-16 md:py-20 border-b border-white/5 bg-[#13161A]">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <h2 className="font-display text-white text-3xl md:text-4xl font-black tracking-[-0.02em]">
                Common deployments in {data.name}
              </h2>
              <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
                {data.use_cases.map((u, i) => {
                  const Icon = USE_ICONS[i % USE_ICONS.length];
                  return (
                    <div key={i} className="bg-[#16191C] p-6">
                      <div className="w-10 h-10 border border-white/10 flex items-center justify-center">
                        <Icon size={16} className="text-[#00FF85]" />
                      </div>
                      <p className="mt-4 text-white text-sm font-body leading-relaxed">{u}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* SLA + CTA */}
          <section className="py-16">
            <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
              <div className="inline-flex items-center gap-2 border border-[#00FF85]/40 bg-[#00FF85]/10 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#00FF85]">
                <ShieldCheck size={12} /> Response SLA · {data.response_sla}
              </div>
              <h2 className="font-display mt-6 text-white text-3xl md:text-5xl font-black tracking-[-0.02em]">
                Ready to install in {data.name}?
              </h2>
              <p className="mt-4 text-[#A1A1AA] max-w-2xl mx-auto">
                Share your site, traffic and timeline. Our local team will spec the right BBC model and reach out within one business day.
              </p>
              <Link
                to="/quote"
                data-testid="city-cta-quote-bottom"
                className="mt-8 inline-flex items-center gap-3 bg-[#00FF85] hover:bg-[#00CC6A] text-black font-semibold px-7 py-4 text-xs uppercase tracking-wider shadow-[0_0_28px_rgba(0,255,133,0.4)] transition"
              >
                Talk to BBC in {data.name} <ArrowRight size={14} />
              </Link>
            </div>
          </section>
        </>
      )}

      <PlatformShellFooter />
    </div>
  );
}
